// Pricing section — the volume slider, denominated in actions (AIT-420).
//
// The slider is the sales surface: someone arrives knowing roughly how much
// they send, drags to that number, and the plan finds them. Static cards make
// them do that arithmetic themselves. Money model v2 only changed the UNIT
// (messages -> actions) and the thresholds, both of which are data here, so
// the interaction below is unchanged from the message-denominated original.
//
// Layout (desktop >=920px, single column on mobile):
//   - Single 24px-radius shell, dual-shadow elevation.
//   - TOP    — live action count + the slider itself.
//   - LEFT  (1.1fr, slightly raised surface) — live spec card:
//     eyebrow "Price / Month" -> big price ($X) -> "Up to X actions/month"
//     -> description -> "Start Free" CTA. The whole left column updates as
//     the slider drags or a plan row is clicked.
//   - RIGHT (1fr, base shell surface) — the plan rows: marker, name, cost.
//     Active row gets Meta Blue background, blue ring, filled marker.
//
// Interaction: native <input type="range"> sits over the visual track with
// pointer-events on (it owns input). Plan rows are <button>s that snap the
// slider to that plan's start position. Keyboard arrows + Home/End work
// for free via the native input.
//
// The trial is deliberately NOT a slider zone: it is 7 days on Business, not
// a volume tier, so pinning it to the low end of a volume axis would misprice
// it. It lives in the sub-headline and the CTA instead.
//
// `pricing-catalog.page.test.ts` locks the price/quota numbers against
// `@hookmyapp/shared` PLAN_CATALOG_V2 so a catalog change can't ship with
// stale marketing copy. The action-definition sentence is verbatim per spec
// Decision 8 — do not reword it.
import { useEffect, useId, useRef, useState } from 'react';
import NumberFlow from '@number-flow/react';

import { emit } from '../lib/analytics';
import { buildHandoffUrl } from '../lib/handoff';

interface Zone {
  label: string;
  from: number;
  to: number;
  price: number | null;
  annual?: string;
  description: string;
}

const ZONES: Zone[] = [
  {
    label: 'Build',
    from: 0,
    to: 200,
    price: 1,
    annual: '$10/year',
    description: 'For solo developers shipping their first integration.',
  },
  {
    label: 'Scale',
    from: 201,
    to: 15000,
    price: 24,
    annual: '$240/year',
    description: 'For growing teams handling customer traffic.',
  },
  {
    label: 'Business',
    from: 15001,
    to: 100000,
    price: 97,
    annual: '$970/year',
    description: 'For businesses running at high volume.',
  },
  {
    label: "Let's Talk",
    from: 100001,
    to: 100001,
    price: null,
    description: 'Enterprise volumes.',
  },
];

const CONTACT_HREF = 'mailto:support@hookmyapp.com';

const SLIDER_MAX = 1000;
// The priced zones — "Let's Talk" is the terminal snap at 100%, not a span
// you can drag through, so it owns no track.
const ZONE_COUNT = ZONES.length - 1;

// How much of the track each zone occupies. The original gave every zone an
// equal slice, which worked when the tiers were 2k/30k/100k/250k messages —
// spans within one order of magnitude of each other. Actions span 200 /
// 14,800 / 85,000, a 425x spread, so equal slices spent a third of the track
// crawling through Build's 200 actions and squeezed the range most people
// actually land in.
//
// Width follows the ORDER OF MAGNITUDE of each zone's span, not the span
// itself. Weighting by the raw span is just as lopsided the other way: Business
// covers 85% of the numbers, so it swallowed three quarters of the track and
// the two plans most people are choosing between were crammed into the first
// fifth. A log weight gives each decade of volume comparable room, so no zone
// dominates and the bigger ranges still get more space than the small ones.
const ZONE_WIDTHS: number[] = (() => {
  const priced = ZONES.slice(0, ZONE_COUNT);
  const raw = priced.map((z) => Math.log10(z.to - z.from + 1));
  const rawTotal = raw.reduce((sum, w) => sum + w, 0);
  return raw.map((w) => w / rawTotal);
})();

/** Track position where each zone begins (the plan rows snap to these). */
const ZONE_STARTS: number[] = ZONE_WIDTHS.map((_, i) =>
  ZONE_WIDTHS.slice(0, i).reduce((sum, w) => sum + w, 0),
);

function pctToActions(pct: number): number {
  const clamped = Math.max(0, Math.min(1, pct));
  if (clamped >= 1) return ZONES[ZONES.length - 1].from;
  let zoneIdx = ZONE_COUNT - 1;
  for (let i = 0; i < ZONE_COUNT; i += 1) {
    if (clamped < ZONE_STARTS[i] + ZONE_WIDTHS[i]) {
      zoneIdx = i;
      break;
    }
  }
  const zone = ZONES[zoneIdx];
  const within = (clamped - ZONE_STARTS[zoneIdx]) / ZONE_WIDTHS[zoneIdx];
  const raw = zone.from + within * (zone.to - zone.from + 1);
  const range = zone.to - zone.from;
  const step = range <= 2999 ? 25 : range <= 49999 ? 250 : 500;
  // Round RELATIVE to the zone's own start, so the first stop in every zone
  // is exactly that plan's opening number (Scale reads 201, not 250). Absolute
  // rounding snapped to the nearest multiple of the step instead, so clicking
  // a plan row landed on a number that plan does not actually start at.
  const rounded = zone.from + Math.round((raw - zone.from) / step) * step;
  return Math.max(zone.from, Math.min(zone.to, rounded));
}

function getZoneIdx(actions: number): number {
  const i = ZONES.findIndex((z) => actions <= z.to);
  return i < 0 ? ZONES.length - 1 : i;
}

export function Pricing() {
  const [sliderValue, setSliderValue] = useState(0);
  const [glide, setGlide] = useState(false);
  const sliderPct = sliderValue / SLIDER_MAX;
  const actions = pctToActions(sliderPct);
  const planIdx = getZoneIdx(actions);
  const plan = ZONES[planIdx];
  const isLetsTalk = plan.label === "Let's Talk";
  const fillPct = sliderPct * 100;
  const sliderId = useId();
  const ctaHref = buildHandoffUrl('');
  const sectionRef = useRef<HTMLElement | null>(null);

  // pricing_viewed fires once when the section first enters the viewport.
  // Mirrors the retired Base44 hookmyapp-marketing behaviour (Pricing.jsx
  // line 70). The observer disconnects after the first capture so the event
  // is never deduplicated client-side.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const node = sectionRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            emit('pricing_viewed', {});
            obs.disconnect();
            return;
          }
        }
      },
      { threshold: 0.2 },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  const selectPlan = (i: number): void => {
    setGlide(true);
    // Terminal zone snaps to the far end; priced zones snap to their own start.
    // Ceil, never round: rounding down by one slider step would land just
    // BEFORE the boundary and select the previous plan.
    setSliderValue(Math.min(SLIDER_MAX, Math.ceil((ZONE_STARTS[i] ?? 1) * SLIDER_MAX)));
    window.setTimeout(() => setGlide(false), 720);
  };

  return (
    <section
      ref={sectionRef}
      id="pricing"
      data-testid="pricing"
      className="relative overflow-hidden bg-gradient-to-b from-black via-[#0E1012] to-black px-6 py-16 md:py-24"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/12 to-transparent"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/3 h-[600px] w-[1000px] -translate-x-1/2 rounded-full bg-[#0064E0]/10 blur-[180px]"
      />
      <div className="relative mx-auto max-w-5xl">
        <div className="mb-14 text-center">
          <h2
            className="text-4xl md:text-5xl font-medium leading-[1.17] text-white"
            style={{ letterSpacing: '-0.015em' }}
          >
            Pricing.
          </h2>
          <p className="mx-auto mt-4 max-w-md text-lg leading-[1.44] text-white/60">
            Slide to find the plan that fits your volume. Every plan starts with
            7 days free on Business, no credit card needed.
          </p>
        </div>

        <div className="mx-auto max-w-[980px] overflow-hidden rounded-[24px] border border-white/[0.08] shadow-[0_12px_28px_rgba(0,0,0,0.4),0_2px_4px_rgba(0,0,0,0.2)]">
          <div className="bg-[#1F2123] border-b border-white/[0.08] px-10 py-9">
            <div className="mb-6 flex flex-col items-center gap-1.5">
              <span
                aria-hidden="true"
                className="flex h-12 items-center font-medium tabular-nums leading-none text-white"
                style={{
                  fontSize: 'clamp(36px, 4vw, 44px)',
                  letterSpacing: '-0.02em',
                }}
              >
                {isLetsTalk ? (
                  <span className="price-fade-in">100,000+</span>
                ) : (
                  <NumberFlow value={actions} className="tabular-nums" />
                )}
              </span>
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/45">
                Actions / Month
              </span>
            </div>
            <div className="relative h-[22px]">
              <div className="pointer-events-none absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-white/10" />
              <div
                className={`pointer-events-none absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-[#0064E0] transition-[width] ${
                  glide
                    ? 'duration-700 ease-[cubic-bezier(0.65,0,0.35,1)]'
                    : 'duration-75 ease-out'
                }`}
                style={{ width: `${fillPct}%` }}
              />
              <div
                className={`pointer-events-none absolute top-1/2 size-[22px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_2px_6px_rgba(0,0,0,0.4),0_0_0_2px_#0064E0,0_0_0_6px_rgba(0,100,224,0.2)] transition-[left] ${
                  glide
                    ? 'duration-700 ease-[cubic-bezier(0.65,0,0.35,1)]'
                    : 'duration-75 ease-out'
                }`}
                style={{ left: `${fillPct}%` }}
              />
              <input
                id={sliderId}
                type="range"
                min={0}
                max={SLIDER_MAX}
                step={1}
                value={sliderValue}
                onChange={(e) => setSliderValue(Number(e.target.value))}
                aria-label="Monthly actions"
                className="absolute inset-x-0 top-1/2 h-[22px] w-full -translate-y-1/2 cursor-pointer appearance-none bg-transparent opacity-0"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 lg:divide-x lg:divide-white/[0.08]">
            <div className="bg-[#1F2123] p-10 max-sm:text-center">
              <p className="text-xs font-bold uppercase tracking-[0.08em] text-white/55">
                Price / Month
              </p>
              <div
                aria-hidden="true"
                className="mt-2 flex h-[64px] items-center font-medium tabular-nums leading-none text-white max-sm:justify-center"
                style={{
                  fontSize: 'clamp(48px, 6vw, 64px)',
                  letterSpacing: '-0.02em',
                }}
              >
                {isLetsTalk ? (
                  <span className="price-fade-in">Custom</span>
                ) : (
                  <NumberFlow
                    value={plan.price ?? 0}
                    prefix="$"
                    className="tabular-nums"
                  />
                )}
              </div>
              <p
                aria-hidden="true"
                className="mt-4 flex flex-wrap items-baseline gap-x-1 gap-y-0.5 text-lg font-semibold leading-snug text-white max-sm:justify-center"
              >
                <span className="flex items-baseline gap-1 whitespace-nowrap">
                  {!isLetsTalk && <span>Up to</span>}
                  <NumberFlow
                    value={plan.to}
                    suffix={isLetsTalk ? '+' : ''}
                    className="tabular-nums"
                  />
                </span>
                <span className="max-sm:basis-full">actions/month</span>
              </p>
              <p className="sr-only">
                {isLetsTalk
                  ? `Custom pricing above ${ZONES[ZONE_COUNT - 1].to.toLocaleString('en-US')} actions per month.`
                  : `$${plan.price} per month for up to ${plan.to.toLocaleString('en-US')} actions per month.`}
              </p>
              <div className="mt-1.5 max-w-[380px] overflow-hidden">
                <p
                  key={planIdx}
                  className="text-roll-in text-[15px] leading-[1.5] text-white/60"
                >
                  {plan.description}
                </p>
              </div>
              {plan.annual && (
                <p className="mt-2 text-sm leading-[1.5] text-white/45">
                  Billed annually: {plan.annual}
                </p>
              )}
              <div className="mt-7 max-sm:text-center">
                <a
                  href={isLetsTalk ? CONTACT_HREF : ctaHref}
                  onClick={() =>
                    emit('cta_clicked', {
                      cta_location: 'pricing',
                      cta_text: isLetsTalk ? 'Talk To Us' : 'Start Free',
                      destination_url: isLetsTalk ? CONTACT_HREF : ctaHref,
                    })
                  }
                  className="inline-flex items-center justify-center rounded-[100px] bg-[#0064E0] hover:bg-[#0143B5] active:bg-[#004BB9] px-7 py-3.5 text-sm font-semibold tracking-[-0.14px] text-white transition active:scale-[0.96]"
                >
                  {isLetsTalk ? 'Talk To Us' : 'Start Free'}
                </a>
              </div>
            </div>

            <div className="bg-[#181A1B] p-8 md:p-9">
              <ul className="flex flex-col gap-2">
                {ZONES.map((z, i) => {
                  const active = i === planIdx;
                  const cost = z.price == null ? 'Custom' : `$${z.price}`;
                  return (
                    <li key={z.label}>
                      <button
                        type="button"
                        onClick={() => selectPlan(i)}
                        aria-pressed={active}
                        className={`flex w-full items-center gap-3 rounded-[100px] border-2 px-4 py-3.5 text-left transition-[background-color,border-color,box-shadow,transform] duration-300 ease-out ${
                          active
                            ? 'border-[#0064E0] bg-[#0064E0]/15 shadow-[0_4px_16px_rgba(0,100,224,0.18)]'
                            : 'border-transparent bg-white/[0.04] hover:bg-white/[0.09]'
                        }`}
                      >
                        <span
                          className={`relative size-[18px] shrink-0 rounded-full border-2 transition-colors duration-300 ease-out ${
                            active
                              ? 'border-[#0064E0] bg-[#0064E0]'
                              : 'border-white/20 bg-transparent'
                          }`}
                        >
                          <span
                            className={`absolute inset-1 rounded-full bg-white transition-[transform,opacity] duration-300 ease-out ${
                              active ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                            }`}
                          />
                        </span>
                        <span
                          className={`text-[15px] font-semibold transition-colors duration-300 ease-out ${
                            active ? 'text-white' : 'text-white/85'
                          }`}
                        >
                          {z.label}
                        </span>
                        <span
                          className={`ml-auto text-sm font-bold tabular-nums transition-colors duration-300 ease-out ${
                            active ? 'text-[#3B86FF]' : 'text-white/55'
                          }`}
                        >
                          {cost}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        <p className="mx-auto mt-8 max-w-2xl text-center text-sm leading-[1.5] text-white/45">
          An action is anything you do that reaches your audience: a message
          sent, a comment replied, a post published. Receiving is always
          free.
        </p>
      </div>
    </section>
  );
}
